import { FC, useEffect, useRef } from 'react';

import { Loader2 } from 'lucide-react';

interface Props {
    loading: boolean;
    onLoadMore: () => void;
    className?: string;
}

export const InfiniteScrollTrigger: FC<Props> = ({
    loading,
    onLoadMore,
    className = '',
}) => {
    const triggerRef = useRef<HTMLDivElement>(null);
    const loadMoreRef = useRef(onLoadMore);

    useEffect(() => {
        loadMoreRef.current = onLoadMore;
    }, [onLoadMore]);

    useEffect(() => {
        const trigger = triggerRef.current;
        if (!trigger || loading) return;

        let requested = false;
        const loadWhenVisible = () => {
            const bounds = trigger.getBoundingClientRect();
            const isNearViewport =
                bounds.top <= window.innerHeight + 400 && bounds.bottom >= -400;

            if (isNearViewport && !requested) {
                requested = true;
                loadMoreRef.current();
            }
        };

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) loadWhenVisible();
            },
            { rootMargin: '400px 0px' },
        );

        observer.observe(trigger);
        window.addEventListener('scroll', loadWhenVisible, { passive: true });
        const frame = window.requestAnimationFrame(loadWhenVisible);

        return () => {
            observer.disconnect();
            window.removeEventListener('scroll', loadWhenVisible);
            window.cancelAnimationFrame(frame);
        };
    }, [loading]);

    return (
        <div
            ref={triggerRef}
            className={`flex min-h-12 items-center justify-center text-sm text-muted-foreground ${className}`}
            aria-live="polite"
            data-testid="infinite-scroll-trigger"
        >
            {loading && (
                <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Завантаження…
                </>
            )}
        </div>
    );
};
